(ns app.front.view.admin.tenant-management.municipalities-tab
  "Municipalities tab of the tenant detail view. A tenant's municipalities come
  from its authority groups' scopes; each row expands into an editor. Saving
  sends the whole list to `:admin/tenant-set-municipalities`, so leaving a
  municipality out removes its scopes."
  (:require [app.domain.group.model :as group-model]
            [app.front.ui :as ui]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.view.admin.domain-groups.common :as domain-groups-common]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [app.front.view.admin.domain-groups.subs :as domain-groups-subs]
            [app.front.view.admin.tenant-management.subs :as tm-subs]
            [app.front.view.workspace.map :as workspace-map]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Reading the tenant's municipalities
;; ---------------------------------------------------------------------------

(defn tenant-municipalities
  "The tenant's municipalities in the shape the edit command expects, i.e. the
  same shape the municipality-groups editor uses."
  [domain-group]
  (->> domain-group
       group-model/descendant-authority-groups
       (mapcat group-model/scope-municipalities)
       distinct
       (mapv mg/editable-municipality)))

(rf/reg-event-fx ::save
  (fn [_ [_ tenant-id municipalities]]
    {:comm/command {:command          :admin/tenant-set-municipalities
                    :data             {:domain-group-id tenant-id
                                       ;; A new municipality has no `:db/id`: the
                                       ;; key must be absent, not nil.
                                       :municipalities  (mapv #(->> (select-keys % [:db/id :name :official-name
                                                                                    :number :location])
                                                                    (remove (comp nil? val))
                                                                    (into {}))
                                                              municipalities)}
                    :notify           {:success :changes-saved}
                    ;; Adding one creates scopes across the tenant's products, so
                    ;; re-pull rather than patching the entity-db.
                    :finally-dispatch [::tm-subs/fetch-tenant tenant-id]}}))

;; ---------------------------------------------------------------------------
;; Editor shared by the row expansion and the add form
;; ---------------------------------------------------------------------------

(defn- location-picker
  "Click the map to place the centre: required for a new municipality, and it
  moves the stored centre of an existing one."
  [tr *draft]
  (let [{:keys [location number]} @*draft]
    [:div.flex.flex-col
     [:div.bx--label (tr [:admin/municipality-location])]
     ;; Roughly the height of the fields beside it once the two sit side by side;
     ;; shorter when it wraps onto its own row below them.
     [:div.w-full.h-64 {:class "lg:h-[25rem]"}
      [workspace-map/workspace-map
       (merge-with merge
                   {:center (or location
                                (<sub [::domain-groups-subs/location-of number])
                                (<sub [::domain-groups-subs/default-location]))}
                   {:events [{:eventType     "singleclick"
                              :eventFunction (fn [e]
                                               (let [location (js->clj (.-coordinate ^js e))]
                                                 (swap! *draft assoc :location location)))}]})]]]))

(defn municipality-fields
  "Name, official name and code beside the centre-location map, for the row
  expansion, the add form and the new-tenant view. `:languages-sub` is a
  zero-arg subscription of the tenant's languages; the selector hides itself
  when there is one. Editing a name drops texts in other languages, as elsewhere
  for group-scoped strings."
  [{:keys [tr *draft id-prefix other-municipalities languages-sub]}]
  (let [{:keys [name official-name number]} @*draft
        id-prefix                           (or id-prefix "tm-muni")
        languages-sub                       (or languages-sub ::ui-i18n/languages)
        unique-number?                      (domain-groups-common/municipality-numbers-unique?
                                              (conj (vec other-municipalities) @*draft))]
    [:div.grid.gap-6 {:class "lg:grid-cols-2"}
     [:div.flex.flex-col.gap-4
      [ui-i18n/edit-language-selector {:allowed-locales-sub languages-sub}]
      ;; `localized-string-edit` auto-focuses by default, which on a page that
      ;; opens with this form would jump past everything above it.
      [ui-i18n/localized-string-edit
       {:id                  (str id-prefix "-name-" (or number "new"))
        :label-text          (tr [:admin/municipality-name])
        :placeholder         (tr [:placeholder/municipality])
        :allowed-locales-sub languages-sub
        :auto-focus          false
        :light               false
        :strings             name
        :on-change           #(swap! *draft assoc :name %)}]
      [ui-i18n/localized-string-edit
       {:id                  (str id-prefix "-official-name-" (or number "new"))
        :label-text          (tr [:admin/official-name])
        :placeholder         (tr [:admin/placeholder-official-name])
        :allowed-locales-sub languages-sub
        :auto-focus          false
        :light               false
        :strings             official-name
        :on-change           #(swap! *draft assoc :official-name %)}]
      [ui/text-input
       {:id           (str id-prefix "-number-" (or number "new"))
        :label-text   (tr [:admin/municipality-id])
        :placeholder  (tr [:placeholder/municipality-code])
        :value        number
        :invalid      (not unique-number?)
        :invalid-text (tr [:admin/municipality-number-not-unique])
        :on-change    #(swap! *draft assoc :number (-> % .-target .-value str/trim))}]]
     [location-picker tr *draft]]))

(defn- valid?
  [coordinates municipalities draft]
  (and (domain-groups-common/valid-municipality? coordinates draft)
       (domain-groups-common/municipality-numbers-unique?
         (conj (vec municipalities) draft))))

;; ---------------------------------------------------------------------------
;; Row expansion
;; ---------------------------------------------------------------------------

(defn- municipality-editor
  [tr {:keys [tenant-id municipality municipalities]}]
  (r/with-let [*draft  (r/atom municipality)
               *resets (r/atom 0)
               revert! (fn [] (reset! *draft municipality) (swap! *resets inc))]
    (let [others (remove #(= (:number municipality) (:number %)) municipalities)
          dirty? (not= municipality @*draft)
          ok?    (valid? (<sub [::domain-groups-subs/coordinates]) others @*draft)]
      [:div.p-2
       ^{:key @*resets}
       [municipality-fields {:tr                   tr
                             :*draft               *draft
                             :languages-sub        ::tm-subs/tenant-languages
                             :other-municipalities others}]
       [:div.flex.justify-between.items-center.mt-6.flex-wrap.gap-2
        [ui/modal-confirmation-button
         {:tr                  tr
          :modal-title         (tr [:admin/remove-municipality])
          :trigger-button-text (tr [:admin/remove-municipality])
          :trigger-button-opts {:size :small :kind :danger :icon i/remove}
          ;; The command rejects an empty list, so the last one can't be removed.
          :disabled?           (= 1 (count municipalities))
          :on-yes-click        #(>evt [::save tenant-id others])}
         (tr [:admin/tenant-remove-municipality-confirmation]
             [(i18n-model/get-text-from-entities-or-map (:name municipality)
                                                        (<sub [:language]))])]
        [:div.flex.gap-2
         [ui/button {:kind     :secondary
                     :size     :small
                     :disabled (not dirty?)
                     :on-click revert!}
          (tr [:cancel])]
         [ui/button {:kind     :primary
                     :size     :small
                     :disabled (not (and dirty? ok?))
                     :on-click #(>evt [::save tenant-id (conj (vec others) @*draft)])}
          (tr [:save])]]]])))

;; ---------------------------------------------------------------------------
;; Add form
;; ---------------------------------------------------------------------------

(defn- add-municipality-panel
  [tr {:keys [tenant-id municipalities default-number default-name on-close]}]
  (r/with-let [*draft (r/atom {:number   default-number
                               :name     {i18n-model/default-lang default-name}
                               :location (<sub [::domain-groups-subs/default-location])})]
    (let [ok? (valid? (<sub [::domain-groups-subs/coordinates]) municipalities @*draft)]
      [:div.border.border-solid.border-ui-violet.rounded.p-4.mb-4
       [:h5.mb-4 (tr [:admin/start-add-municipality])]
       [municipality-fields {:tr                   tr
                             :*draft               *draft
                             :languages-sub        ::tm-subs/tenant-languages
                             :other-municipalities municipalities}]
       [:div.flex.justify-end.gap-2.mt-6
        [ui/button {:kind :secondary :size :small :on-click on-close}
         (tr [:cancel])]
        [ui/button {:kind     :primary
                    :size     :small
                    :disabled (not ok?)
                    :on-click #(do (>evt [::save tenant-id (conj (vec municipalities) @*draft)])
                                   (on-close))}
         (tr [:admin/add-municipality-to-group])]]])))

;; ---------------------------------------------------------------------------
;; Table
;; ---------------------------------------------------------------------------

(defn- municipalities-table [tr tenant-id municipalities]
  (let [lang    (<sub [:language])
        get-str #(i18n-model/get-text-from-entities-or-map % lang)]
    [sortable-table/table
     {:tr              tr
      :id              "admin-tenant-municipalities-table"
      :items           municipalities
      :id-func         :number
      :initial-sorting [:number :asc]
      :expanded-func   (fn [municipality]
                         [municipality-editor tr (util/mmap tenant-id municipality municipalities)])
      :cols            [[:admin/municipality-name-short
                         (comp get-str :name)
                         {:sorting (fn [a b] (compare (get-str (:name a)) (get-str (:name b))))}]
                        [:admin/official-name-short
                         (comp get-str :official-name)
                         {:truncate? true}]
                        [:admin/municipality-id :number {:sorting true :width "180px"}]]}]))

(defn municipalities-tab [tr {:keys [group active?]}]
  (r/with-let [*adding? (r/atom false)]
    (let [tenant-id      (:db/id group)
          municipalities (tenant-municipalities group)]
      [:section.m-0.mt-6
       [:p.text-gray-600.mb-4 (tr (if active?
                                    [:admin/tenant-municipalities-help]
                                    [:admin/tenant-inactive-read-only]))]
       (cond
         (not active?)
         nil

         @*adding?
         [add-municipality-panel tr {:tenant-id      tenant-id
                                     :municipalities municipalities
                                     ;; The bare number, as the municipality-groups editor prefills it.
                                     :default-number (mg/group-id->group-number (:group/id group))
                                     :default-name   (:group/name group)
                                     :on-close       #(reset! *adding? false)}]

         :else
         [:div.flex.justify-end.mb-4
          [ui/button {:kind     :primary
                      :size     :small
                      :icon     i/plus
                      :on-click #(reset! *adding? true)}
           (tr [:admin/start-add-municipality])]])
       (if (seq municipalities)
         [municipalities-table tr tenant-id municipalities]
         [:p.text-gray-500 (tr [:none])])])))
